import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGuestRsvpManage, useUpdateGuestRsvp, useCancelGuestRsvp } from '../hooks/useGuestRsvpManage'
import type { DishCategory, RsvpAttendanceStatus } from '@fellowship/shared'

const ATTENDANCE_LABELS: Record<RsvpAttendanceStatus, string> = {
  going: 'Going',
  maybe: 'Maybe',
  not_going: 'Not going',
}

const DISH_LABELS: Record<DishCategory, string> = {
  main: 'Main dish',
  salad: 'Salad',
  dessert: 'Dessert',
  drink: 'Drink',
  side: 'Side dish',
  none: "I won't bring a dish",
}

export default function GuestRsvpManagePage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useGuestRsvpManage(token!)
  const updateMutation = useUpdateGuestRsvp(token!)
  const cancelMutation = useCancelGuestRsvp(token!)

  const [cancelled, setCancelled] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading your RSVP…</p>
      </div>
    )
  }

  if (error) {
    const isExpired = (error as { status?: number }).status === 410
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {isExpired ? 'Link expired' : 'RSVP not found'}
          </h1>
          <p className="text-gray-500 text-sm">
            {isExpired
              ? 'This manage link is only valid for 48 hours after the event.'
              : 'This link is invalid or has already been used.'}
          </p>
        </div>
      </div>
    )
  }

  if (!data) return null

  if (cancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">RSVP cancelled</h1>
          <p className="text-gray-500 text-sm">
            Your RSVP for <strong>{data.event.title}</strong> has been cancelled.
          </p>
        </div>
      </div>
    )
  }

  const eventDate = new Date(data.event.startsAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  function handleAttendanceChange(status: RsvpAttendanceStatus) {
    updateMutation.mutate({ attendanceStatus: status }, {
      onSuccess: () => {
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 2000)
      },
    })
  }

  function handleDishChange(category: DishCategory) {
    updateMutation.mutate({ dishCategory: category }, {
      onSuccess: () => {
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 2000)
      },
    })
  }

  function handleCancel() {
    if (!window.confirm('Are you sure you want to cancel your RSVP?')) return
    cancelMutation.mutate(undefined, {
      onSuccess: () => setCancelled(true),
    })
  }

  const rsvp = data.rsvp

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow p-6 mb-4">
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Manage your RSVP</h1>
          <p className="text-sm text-gray-500">{data.event.title}</p>
          <p className="text-sm text-gray-400 mt-1">{eventDate}</p>
        </div>

        <div className="bg-white rounded-xl shadow p-6 mb-4 space-y-6">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Attendance</p>
            <div className="flex gap-2 flex-wrap">
              {(['going', 'maybe', 'not_going'] as RsvpAttendanceStatus[]).map((status) => (
                <button
                  key={status}
                  onClick={() => handleAttendanceChange(status)}
                  disabled={updateMutation.isPending}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    rsvp.attendanceStatus === status
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {ATTENDANCE_LABELS[status]}
                </button>
              ))}
            </div>
          </div>

          {data.event.hasDishCoordination && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Dish</p>
              <select
                value={rsvp.dishCategory}
                onChange={(e) => handleDishChange(e.target.value as DishCategory)}
                disabled={updateMutation.isPending}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(Object.keys(DISH_LABELS) as DishCategory[]).map((cat) => (
                  <option key={cat} value={cat}>{DISH_LABELS[cat]}</option>
                ))}
              </select>
            </div>
          )}

          {saveSuccess && (
            <p className="text-green-600 text-sm font-medium" role="status">Saved!</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-sm text-gray-500 mb-3">
            Can't make it? You can cancel your RSVP below.
          </p>
          <button
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            className="w-full py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Cancel RSVP'}
          </button>
        </div>
      </div>
    </div>
  )
}
